// Knowledge graph data hook - fetches nodes and edges
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface KGNode {
  id: string
  organization_id: string
  entity_type: 'issue' | 'knowledge_entry' | 'resolution_method'
  entity_id: string
  label: string
  properties: Record<string, unknown>
  embedding: number[] | null
  created_at: string
  updated_at: string
}

export interface KGEdge {
  id: string
  organization_id: string
  source_node_id: string
  target_node_id: string
  relationship_type: 'resolved_by' | 'related_to' | 'similar_to' | 'depends_on'
  properties: Record<string, unknown>
  weight: number
  created_at: string
  updated_at: string
}

export interface KnowledgeGraphData {
  nodes: KGNode[]
  edges: KGEdge[]
}

interface UseKnowledgeGraphOptions {
  entityTypes?: Array<'issue' | 'knowledge_entry' | 'resolution_method'>
  limit?: number
}

export function useKnowledgeGraph(options: UseKnowledgeGraphOptions = {}) {
  const { entityTypes, limit = 100 } = options

  const nodesQuery = useQuery({
    queryKey: ['kg-nodes', entityTypes, limit],
    queryFn: async () => {
      let query = supabase
        .from('kg_node')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit)

      if (entityTypes && entityTypes.length > 0) {
        query = query.in('entity_type', entityTypes)
      }

      const { data, error } = await query
      if (error) throw error
      return data as KGNode[]
    },
  })

  const edgesQuery = useQuery({
    queryKey: ['kg-edges', nodesQuery.data?.map(n => n.id)],
    queryFn: async () => {
      if (!nodesQuery.data || nodesQuery.data.length === 0) {
        return []
      }

      const nodeIds = nodesQuery.data.map(n => n.id)
      const { data, error } = await supabase
        .from('kg_edge')
        .select('*')
        .or(`source_node_id.in.(${nodeIds.join(',')}),target_node_id.in.(${nodeIds.join(',')})`)

      if (error) throw error
      return data as KGEdge[]
    },
    enabled: !!nodesQuery.data && nodesQuery.data.length > 0,
  })

  return {
    nodes: nodesQuery.data ?? [],
    edges: edgesQuery.data ?? [],
    isLoading: nodesQuery.isLoading || edgesQuery.isLoading,
    error: nodesQuery.error || edgesQuery.error,
    refetch: () => {
      nodesQuery.refetch()
      edgesQuery.refetch()
    },
  }
}
